/**
 * MOQ (Minimum Order Quantity) Utility Functions
 * Handles calculations and validation for product minimum order quantities
 */

class MOQUtils {
    /**
     * Get the MOQ value for a product
     * @param {number} productId - The Shopify product ID
     * @returns {number} The MOQ value, defaults to 1 if not set
     */
    static getMOQ(productId) {
        const moqElement = document.querySelector(`[data-moq-product="${productId}"]`);
        const moq = moqElement ? parseInt(moqElement.dataset.moq) : 1;
        return Math.max(moq, 1); // Ensure MOQ is at least 1
    }

    /**
     * Calculate the next valid quantity based on MOQ
     * @param {number} currentQuantity - Current quantity value
     * @param {number} moq - Minimum order quantity
     * @param {string} direction - 'up' or 'down' for increment/decrement
     * @returns {number} Next valid quantity
     */
    static getNextValidQuantity(currentQuantity, moq, direction = 'up') {
        if (moq <= 1) return currentQuantity;

        const current = Math.max(parseInt(currentQuantity) || moq, moq);

        if (direction === 'up') {
            return Math.ceil(current / moq) * moq + (current % moq === 0 ? moq : 0);
        } else {
            const prevMultiple = Math.floor(current / moq) * moq;
            return prevMultiple < moq ? moq : prevMultiple;
        }
    }

    /**
     * Get the nearest valid MOQ quantity
     * @param {number} quantity - Desired quantity
     * @param {number} moq - Minimum order quantity
     * @returns {number} Nearest valid MOQ quantity
     */
    static getNearestValidQuantity(quantity, moq) {
        if (moq <= 1) return Math.max(quantity, 1);

        const qty = Math.max(parseInt(quantity) || moq, moq);
        const remainder = qty % moq;

        if (remainder === 0) {
            return qty;
        }

        // Round up to next MOQ multiple
        return Math.ceil(qty / moq) * moq;
    }

    /**
     * Validate if a quantity is valid for the given MOQ
     * @param {number} quantity - Quantity to validate
     * @param {number} moq - Minimum order quantity
     * @returns {boolean} True if quantity is valid
     */
    static isValidMOQQuantity(quantity, moq) {
        if (moq <= 1) return quantity >= 1;

        const qty = parseInt(quantity);
        return qty >= moq && qty % moq === 0;
    }

    /**
     * Get MOQ display text for the quantity selector
     * @param {number} moq - Minimum order quantity
     * @returns {string} Display text for MOQ
     */
    static getMOQDisplayText(moq) {
        if (moq <= 1) return '';
        return `Minimum order quantity: ${moq}`;
    }

    /**
     * Get the step value for quantity inputs based on MOQ
     * @param {number} moq - Minimum order quantity
     * @returns {number} Step value for input
     */
    static getMOQStep(moq) {
        return Math.max(moq, 1);
    }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MOQUtils;
}

// Make available globally
window.MOQUtils = MOQUtils;